// The customer portal's SAFed section (Session BDB, Part 4): per lift, the supplementary safety tests recommended, authorised, booked
// and done, the covering certificate once a job is approved, and a short explainer. Loads when the Testing tab opens (its own
// endpoint). Test names and plain states only: no wording from the source of a recommendation, no price (it is on the quotation) and
// no engineer. A failure never blanks the page.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { PortalSiteSafed } from "../../lib/api";
import { PortalViewDocumentButton } from "./PortalDocumentViewer";
import { SAFED_PORTAL_EMPTY, SAFED_PORTAL_EXPLAINER, SAFED_PORTAL_HEADING } from "../../lib/safedPortalCopy";

const PILL: Record<string, string> = {
  recommended: "ele-pill--amber",
  authorised: "ele-pill--blue",
  booked: "ele-pill--blue",
  done: "ele-pill--green",
};

export function PortalSafedSection({ siteId }: { siteId: string }) {
  const [safed, setSafed] = useState<PortalSiteSafed | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    try {
      api.portal
        .siteSafed(siteId)
        .then((s) => {
          if (live) setSafed(s);
        })
        .catch(() => {
          if (live) setFailed(true);
        });
    } catch {
      setFailed(true);
    }
    return () => {
      live = false;
    };
  }, [siteId]);

  return (
    <section aria-label={SAFED_PORTAL_HEADING} className="mb-5">
      <h3 className="mb-2 font-heading text-lg text-ele-white">{SAFED_PORTAL_HEADING}</h3>
      {SAFED_PORTAL_EXPLAINER.map((p) => (
        <p key={p} className="mb-2 text-sm text-ele-body">{p}</p>
      ))}
      {failed ? (
        <p className="text-sm text-ele-body">This could not be loaded just now. Please try again later.</p>
      ) : !safed ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : safed.lifts.length === 0 ? (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">{SAFED_PORTAL_EMPTY}</p>
      ) : (
        <ul className="space-y-3">
          {safed.lifts.map((lift) => (
            <li key={lift.liftRef ?? "lift"} className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
              <p className="border-b border-white/10 px-4 py-2 font-heading text-ele-white">{lift.liftRef ?? "Lift"}</p>
              <ul>
                {lift.tests.map((t) => (
                  <li key={t.name} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-2 last:border-0">
                    <span className="text-sm text-ele-white">{t.name}</span>
                    <span className="flex items-center gap-2 text-xs text-ele-body">
                      {t.lastTestDisplay ? `Last test: ${t.lastTestDisplay}` : null}
                      <span className={`ele-pill ${PILL[t.state] ?? "ele-pill--grey"}`}>{t.stateLabel}</span>
                    </span>
                  </li>
                ))}
              </ul>
              {lift.certificates.length > 0 && (
                <ul className="border-t border-white/10">
                  {lift.certificates.map((c) => (
                    <li key={c.jobId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2">
                      <span className="text-sm text-ele-white">
                        Certificate {c.reference}
                        {c.dateDisplay ? ` | ${c.dateDisplay}` : ""}
                      </span>
                      <PortalViewDocumentButton href={api.portal.safedCertificateHref(c.jobId)} title={`Certificate ${c.reference}`} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
